import { useEffect, useState } from "react";
import { Flame, Lock, Droplets, TrendingDown, MessageCircle, Send, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { TierBadge } from "@/components/TierBadge";

const REACTS = [["fire", Flame, "Fire", "text-orange-400"], ["lock", Lock, "Lock", "text-emerald-400"], ["sweat", Droplets, "Sweat", "text-sky-400"], ["fade", TrendingDown, "Fade", "text-red-400"]];

export function TicketChat({ parlayId, defaultOpen = false, summary }) {
  const [open, setOpen] = useState(defaultOpen);
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => api.get(`/tickets/${parlayId}/chat`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { if (open) load(); }, [open, parlayId]); // eslint-disable-line react-hooks/exhaustive-deps
  const react = async (key) => {
    try {
      const { data } = await api.post(`/tickets/${parlayId}/react`, { key });
      setD(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tickets/${parlayId}/comments`, { text: text.trim() });
      setD(data);
      setText("");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  const total = d ? d.count : summary?.comments ?? 0;
  const reacts = d ? Object.values(d.reactions).reduce((a, b) => a + b, 0) : summary?.reactions ?? 0;

  return (
    <div data-testid="ticket-chat" className="rounded-lg border border-[#26334D] bg-[#0B0E14]/50">
      <button data-testid="ticket-chat-toggle" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-400 hover:text-white">
        <MessageCircle size={13} className="text-cyan-400" /> Crew talk <span className="font-mono-num text-slate-500" data-testid="ticket-chat-counts">· {total} comment{total !== 1 ? "s" : ""} · {reacts} reaction{reacts !== 1 ? "s" : ""}</span>
        <ChevronDown size={13} className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="fade-up border-t border-[#26334D]/60 px-3 py-2">
          {err && <p className="py-2 text-xs text-slate-500" data-testid="ticket-chat-error">{err}</p>}
          {d && (
            <>
              <div className="flex flex-wrap gap-1.5" data-testid="ticket-reactions">
                {REACTS.map(([key, Icon, label, color]) => {
                  const mine = d.mine.includes(key);
                  return (
                    <button key={key} data-testid={`react-${key}`} data-active={mine} onClick={() => react(key)} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold transition-[background-color,border-color,transform] duration-150 hover:-translate-y-0.5 ${mine ? "border-cyan-400/60 bg-cyan-400/15 text-white" : "border-[#26334D] text-slate-400 hover:text-white"}`}>
                      <Icon size={11} className={color} /> {label} <span className="font-mono-num" data-testid={`react-${key}-count`}>{d.reactions[key] || 0}</span>
                    </button>
                  );
                })}
              </div>
              <ul className="mt-2 max-h-48 space-y-1.5 overflow-y-auto" data-testid="ticket-comments">
                {d.comments.map((c) => (
                  <li key={c.comment_id} data-testid="ticket-comment" className="rounded-md bg-[#121824] px-2.5 py-1.5 text-xs">
                    <span className="flex items-center gap-1.5"><span className="font-semibold text-white">{c.user_name}</span><TierBadge tier={c.tier} /><span className="ml-auto text-[10px] text-slate-600">{new Date(c.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span></span>
                    <p className="mt-0.5 text-slate-300">{c.text}</p>
                  </li>
                ))}
                {d.comments.length === 0 && <li className="py-1 text-xs text-slate-600" data-testid="ticket-comments-empty">No comments yet — be the first to call it.</li>}
              </ul>
              <form onSubmit={send} className="mt-2 flex gap-2">
                <input data-testid="ticket-comment-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={280} placeholder="Tail it? Fade it? Say it." className="input-dark flex-1 !py-1.5 text-xs" />
                <button data-testid="ticket-comment-send" disabled={busy || !text.trim()} className="btn-primary !px-3 !py-1.5 text-xs"><Send size={13} /></button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}
